import { useEffect, useState } from "react";
import { Instagram, Facebook, Loader2, X, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { socialIgStatus, socialIgConnect, socialIgSettings, socialFbConfig, socialIgLoginConfig } from "@/lib/api";

function InstagramLoginRow({ st, userRecordId, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [appId, setAppId] = useState("");
  const [secret, setSecret] = useState("");
  const [busy, setBusy] = useState(false);
  const configured = !!st?.instagram_login_configured;

  async function save(id, sec) {
    setBusy(true);
    try {
      const r = await socialIgLoginConfig(userRecordId, id, sec);
      onSaved(r); setEditing(false); setAppId(""); setSecret("");
      toast.success(id ? "Instagram login credentials saved — dealers can now connect" : "Reverted to server default");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
    } finally { setBusy(false); }
  }

  return (
    <div className="mt-4 text-xs" data-testid="ig-login-config">
      <div className="flex items-center gap-2">
        <Instagram size={14} className="text-pink-600" />
        <span className="font-medium text-zinc-700">Dealer "Connect Instagram" (Business Login for Instagram):</span>
        {configured ? <span className="text-emerald-700 font-semibold" data-testid="ig-login-status">enabled · App ID {st.instagram_login_app_id}</span>
          : <span className="text-amber-700 font-semibold" data-testid="ig-login-status">not configured</span>}
        {!editing && <button type="button" className="text-blue-700 hover:underline" onClick={() => setEditing(true)} data-testid="ig-login-edit">{configured ? "Change" : "Set up"}</button>}
        {!editing && st?.instagram_login_source === "admin" && (
          <button type="button" className="text-zinc-500 hover:underline" disabled={busy} onClick={() => save("", "")} data-testid="ig-login-reset">Use server default</button>
        )}
      </div>
      {editing ? (
        <div className="mt-2 pl-6 space-y-2">
          <p className="text-zinc-500">Meta dashboard → Instagram → API setup with Instagram login → <b>Business login settings</b>. Copy the <b>Instagram App ID</b> and <b>Instagram App Secret</b> (not the Meta App ID), and add this redirect URI there: <code className="font-mono bg-zinc-100 rounded px-1 break-all">{window.location.origin}/api/auth/instagram/callback</code></p>
          <div className="flex flex-wrap items-center gap-2">
            <Input value={appId} onChange={(e) => setAppId(e.target.value.replace(/\D/g, ""))} placeholder="Instagram App ID" className="h-8 w-48 rounded-md text-xs font-mono" data-testid="ig-login-app-id" />
            <Input type="password" value={secret} onChange={(e) => setSecret(e.target.value)} placeholder="Instagram App Secret" className="h-8 w-64 rounded-md text-xs font-mono" data-testid="ig-login-secret" />
            <Button size="sm" disabled={busy || !appId || !secret} onClick={() => save(appId, secret)} className="h-8 rounded-md bg-pink-600 hover:bg-pink-700 text-white px-3" data-testid="ig-login-save">
              {busy ? <Loader2 className="animate-spin" size={12} /> : "Save"}
            </Button>
            <Button size="sm" variant="ghost" className="h-8 rounded-md px-2" onClick={() => { setEditing(false); setAppId(""); setSecret(""); }} data-testid="ig-login-cancel">Cancel</Button>
          </div>
        </div>
      ) : (
        <p className="mt-1 pl-6 text-zinc-500">Lets approved dealers link their own Instagram Business account (Manage → Settings) and post review cards / Stories to their feed.</p>
      )}
    </div>
  );
}

function FacebookConfigRow({ st, userRecordId, onSaved }) {
  const [val, setVal] = useState("");
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const current = st?.facebook_config_id || "";
  const isAdminOverride = st?.facebook_config_id_source === "admin";

  async function save(next) {
    setBusy(true);
    try {
      const r = await socialFbConfig(userRecordId, next);
      onSaved(r); setEditing(false); setVal("");
      toast.success(next ? "Facebook Configuration ID saved" : "Reverted to server default");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save Configuration ID");
    } finally { setBusy(false); }
  }

  return (
    <div className="mt-2 pl-6 text-xs" data-testid="fb-config-row">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-zinc-500">Login for Business Configuration ID:</span>
        {editing ? (
          <>
            <Input value={val} onChange={(e) => setVal(e.target.value.replace(/\D/g, ""))} placeholder={current || "e.g. 1430098779185337"} className="h-7 w-48 rounded-md text-xs font-mono" data-testid="fb-config-input" />
            <Button size="sm" disabled={busy || !val} onClick={() => save(val)} className="h-7 rounded-md bg-blue-600 hover:bg-blue-700 text-white px-2" data-testid="fb-config-save">
              {busy ? <Loader2 className="animate-spin" size={12} /> : "Save"}
            </Button>
            <Button size="sm" variant="ghost" className="h-7 rounded-md px-2" onClick={() => { setEditing(false); setVal(""); }} data-testid="fb-config-cancel">Cancel</Button>
          </>
        ) : (
          <>
            <code className="font-mono text-zinc-800 bg-zinc-100 rounded px-1.5 py-0.5" data-testid="fb-config-value">{current || "not set"}</code>
            <span className="text-[10px] uppercase tracking-wide text-zinc-400" data-testid="fb-config-source">{isAdminOverride ? "admin override" : "server default"}</span>
            <button type="button" className="text-blue-700 hover:underline" onClick={() => setEditing(true)} data-testid="fb-config-edit">Change</button>
            {isAdminOverride && (
              <button type="button" className="text-zinc-500 hover:underline" disabled={busy} onClick={() => save("")} data-testid="fb-config-reset">Use server default</button>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default function SocialSettings({ userRecordId }) {
  const [st, setSt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState("");
  const [saving, setSaving] = useState(false);
  const [replacing, setReplacing] = useState(false);

  useEffect(() => {
    socialIgStatus(userRecordId).then(setSt).catch(() => setSt(null)).finally(() => setLoading(false));
  }, [userRecordId]);

  async function connect() {
    if (!token.trim()) return;
    setSaving(true);
    try {
      const r = await socialIgConnect(userRecordId, token.trim());
      setSt((s) => ({ ...(s || {}), ...r }));
      setToken(""); setReplacing(false);
      toast.success(`Instagram @${r.username} connected`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not connect Instagram");
    } finally { setSaving(false); }
  }

  async function patch(p) {
    try {
      const r = await socialIgSettings(userRecordId, p);
      setSt((s) => ({ ...(s || {}), ...r }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
    }
  }

  if (loading) return <Loader2 className="animate-spin text-zinc-400" size={16} />;
  const connected = !!st?.connected;

  return (
    <div className="mt-4 pt-4 border-t border-zinc-100" data-testid="social-settings">
      <div className="flex items-center gap-2 mb-1">
        <Instagram size={15} className="text-pink-600" />
        <span className="text-sm font-semibold text-zinc-800">Instagram — review auto-posting</span>
        {connected && (
          <span className="text-[10px] font-bold uppercase tracking-wide text-emerald-700 bg-emerald-50 rounded-full px-2 py-0.5" data-testid="ig-connected-badge">
            Connected · @{st.username}{st.days_left != null ? ` · ${st.days_left}d` : ""}
          </span>
        )}
      </div>
      <p className="text-xs text-zinc-500 mb-3">
        New customer reviews are rendered as a branded review card and posted to OreVital's Instagram Business account.
        The access token is stored encrypted and auto-refreshed.
      </p>

      {connected && !replacing ? (
        <div className="flex flex-wrap items-center gap-2 mb-3">
          <Button size="sm" variant="ghost" className="h-8 rounded-lg text-violet-700 hover:bg-violet-50" onClick={() => setReplacing(true)} data-testid="ig-replace-token">
            <RefreshCw size={13} className="mr-1" /> Replace token
          </Button>
          <Button size="sm" variant="ghost" className="h-8 rounded-lg text-rose-600 hover:bg-rose-50" onClick={() => patch({ disconnect: true })} data-testid="ig-disconnect">
            <X size={13} className="mr-1" /> Disconnect
          </Button>
        </div>
      ) : (
        <div className="flex items-center gap-2 mb-3">
          <Input type="password" value={token} onChange={(e) => setToken(e.target.value)} placeholder="Paste Instagram access token (IG…)" className="h-9 rounded-lg text-sm font-mono" data-testid="ig-token-input" />
          <Button size="sm" disabled={saving || !token.trim()} onClick={connect} className="h-9 rounded-lg bg-pink-600 hover:bg-pink-700 text-white" data-testid="ig-token-save">
            {saving ? <Loader2 className="animate-spin" size={14} /> : "Connect"}
          </Button>
          {connected && <Button size="sm" variant="ghost" className="h-9 rounded-lg" onClick={() => { setReplacing(false); setToken(""); }}>Cancel</Button>}
        </div>
      )}

      {connected && (
        <div className="flex flex-wrap items-center gap-3 text-xs" data-testid="ig-autopost-settings">
          <label className="inline-flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={!!st.auto_post} onChange={(e) => patch({ auto_post: e.target.checked })} className="h-4 w-4 accent-pink-600" data-testid="ig-autopost-toggle" />
            <span className="font-medium text-zinc-700">Auto-post new reviews</span>
          </label>
          <label className="inline-flex items-center gap-1.5">
            <span className="text-zinc-500">minimum</span>
            <select value={st.min_stars} onChange={(e) => patch({ min_stars: Number(e.target.value) })} className="h-7 rounded-md border border-zinc-200 px-1.5 text-xs" data-testid="ig-min-stars">
              {[3, 4, 5].map((n) => <option key={n} value={n}>{n}★</option>)}
            </select>
            <span className="text-zinc-500">and 40+ characters</span>
          </label>
        </div>
      )}

      <div className="mt-4 flex items-center gap-2 text-xs text-zinc-500" data-testid="fb-login-status">
        <Facebook size={14} className="text-blue-600" />
        <span className="font-medium text-zinc-700">"Continue with Facebook" sign-in:</span>
        {st?.facebook_login_configured ? (
          <span className="text-emerald-700 font-semibold">enabled</span>
        ) : (
          <span>waiting for the Meta <b>App ID</b> (App Secret is already stored) — paste it to the agent to enable</span>
        )}
      </div>
      {st?.facebook_login_configured && (
        <FacebookConfigRow st={st} userRecordId={userRecordId} onSaved={(r) => setSt((s) => ({ ...(s || {}), ...r }))} />
      )}
      <InstagramLoginRow st={st} userRecordId={userRecordId} onSaved={(r) => setSt((s) => ({ ...(s || {}), ...r }))} />
    </div>
  );
}
